import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';

export type EntryMode = 'ORDER' | 'QUANTITY' | 'USER';

export interface EventProductTarget {
  targetId: string;
  eventId: string;
  productId: string;
  productName?: string;
  entryMode: EntryMode;
  minOrderAmount: number | null;
  minQuantity: number | null;
  isActive: boolean;
  createdAt: string;
}

export interface EventTargetRequest {
  productId: string;
  entryMode: EntryMode;
  minOrderAmount?: number | null;
  minQuantity?: number | null;
}

const BASE = '/admin/v1/events';

export const eventTargetsQueryKey = (eventId: string) =>
  ['admin', 'event-targets', eventId] as const;

export const useEventTargetsQuery = (eventId: string) =>
  useQuery<EventProductTarget[]>({
    queryKey: eventTargetsQueryKey(eventId),
    queryFn: async () => {
      if (!eventId || eventId === 'new') return [];
      const res = await adminFetch(`${BASE}/${eventId}/targets`);
      if (!res.ok) throw new Error(`targets fetch failed: ${res.status}`);
      const env = (await res.json()) as { data?: EventProductTarget[] };
      return Array.isArray(env.data) ? env.data : [];
    },
    enabled: !!eventId && eventId !== 'new',
  });

export const useEventTargetMutation = (eventId: string) => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: eventTargetsQueryKey(eventId) });

  const add = useMutation({
    mutationFn: async (body: EventTargetRequest) => {
      const res = await adminFetch(`${BASE}/${eventId}/targets`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`target add failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ targetId, body }: { targetId: string; body: Partial<EventTargetRequest> }) => {
      const res = await adminFetch(`${BASE}/${eventId}/targets/${targetId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`target update failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (targetId: string) => {
      const res = await adminFetch(`${BASE}/${eventId}/targets/${targetId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`target delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { add, update, remove };
};
